// Meeting page: live or static transcript, summary, lifecycle
// actions. Subscribes to the meeting's SSE stream while active.

import { html, LitElement } from "lit";
import { ApiError, hosted, meetings } from "../lib/api";
import { MeetingStream } from "../lib/sse";
import { navigate, store } from "../lib/store";
import type { Meeting, MeetingEvent, Participant, Segment, SummaryRow } from "../lib/types";

export class MmMeetingDetail extends LitElement {
  static override properties = {
    meetingId: { attribute: false },
    route: { attribute: false },
  };

  meetingId = 0;
  route = "";

  private meeting: Meeting | null = null;
  private segments: Segment[] = [];
  private summary: SummaryRow | null = null;
  private timelineEvents: MeetingEvent[] = [];
  private stream: MeetingStream | null = null;
  private providerError = "";
  private importProgress: { done: number; total: number } | null = null;
  private highlightMs: number | null = null;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("recording-stopped", () => void this.load());
    this.addEventListener("summary-regenerating", () => void this.load());
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stream?.stop();
  }

  override willUpdate(changed: Map<string, unknown>): void {
    if (changed.has("meetingId") && changed.get("meetingId") !== undefined) {
      this.stream?.stop();
      this.stream = null;
      void this.load();
    }
    const t = this.route.match(/[?&]t=(\d+)/);
    this.highlightMs = t ? Number(t[1]) : null;
  }

  private selfParticipant: Participant | null = null;

  private async load(): Promise<void> {
    try {
      const detail = await meetings.detail(this.meetingId);
      this.meeting = detail.meeting;
      this.segments = detail.segments;
      this.summary = detail.summary;
      this.timelineEvents = detail.events ?? [];
      // Hosted + live: the owner re-enters the call (their participant
      // row exists from creation; rejoin via the code re-activates it).
      if (
        detail.meeting.kind === "hosted" &&
        detail.meeting.state === "recording" &&
        detail.meeting.join_code
      ) {
        const me = store.get().user;
        const list = await hosted.participants(this.meetingId);
        this.selfParticipant =
          list.participants.find((p) => p.user_id === me?.id) ?? null;
      }
      this.requestUpdate();
      this.ensureStream();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) navigate("/");
    }
  }

  /** Follow live updates while the meeting is in an active state. */
  private ensureStream(): void {
    const active =
      this.meeting &&
      ["recording", "importing", "summarizing"].includes(this.meeting.state);
    if (!active || this.stream) return;
    // Live hosted meetings delegate to <mm-call>, which runs its own
    // stream — a second EventSource here is wasted connection budget.
    if (
      this.meeting!.kind === "hosted" &&
      this.meeting!.state === "recording" &&
      this.selfParticipant
    ) {
      return;
    }
    this.stream = new MeetingStream(this.meetingId, {
      segment: (s) => {
        // Out-of-order chunks: keep sorted by start_ms.
        if (!this.segments.some((x) => x.id === s.id)) {
          this.segments = [...this.segments, s].sort(
            (a, b) => a.start_ms - b.start_ms || a.id - b.id,
          );
          this.requestUpdate();
        }
      },
      status: (m) => {
        if (this.meeting && m.state) {
          this.meeting = { ...this.meeting, ...m } as Meeting;
          if (["complete", "failed"].includes(m.state)) {
            this.stream?.stop();
            this.stream = null;
            void this.load(); // pick up final summary + billing
          }
          this.requestUpdate();
        }
      },
      summary: (s) => {
        this.summary = s;
        this.requestUpdate();
      },
      providerError: (e) => {
        this.providerError = e.message;
        this.requestUpdate();
      },
      importProgress: (p) => {
        this.importProgress = p;
        this.requestUpdate();
      },
    });
    this.stream.start();
  }

  private async rename(): Promise<void> {
    if (!this.meeting) return;
    const title = prompt("Meeting title", this.meeting.title);
    if (!title?.trim()) return;
    const updated = await meetings.rename(this.meetingId, title.trim());
    this.meeting = updated.meeting;
    this.requestUpdate();
  }

  private async removeMeeting(): Promise<void> {
    if (!confirm("Delete this meeting and its transcript?")) return;
    await meetings.remove(this.meetingId);
    navigate("/");
  }

  private async stopAndSummarize(): Promise<void> {
    await meetings.stop(this.meetingId);
    await this.load();
  }

  override render() {
    const m = this.meeting;
    if (!m) return html`<section><p class="empty">Loading…</p></section>`;
    // Hosted + live → full call view replaces the detail page.
    if (m.kind === "hosted" && m.state === "recording" && this.selfParticipant) {
      return html`<mm-call
        .meetingId=${m.id}
        .selfParticipantId=${this.selfParticipant.id}
        .guestToken=${null}
        .meetingTitle=${m.title}
        .isHost=${this.selfParticipant.role === "host"}
      ></mm-call>`;
    }
    return html`
      <section class="summary">
        <p class="eyebrow">Meeting</p>
        <div class="timeline-head">
          <h1>${m.title}</h1>
          <span class="badge ${m.state === "complete" ? "badge-ok" : "badge-accent"}"
            >${m.state}</span
          >
        </div>
        <p class="muted">
          ${new Date(m.created_at).toLocaleString()} · ${m.billed_stt_seconds}s
          transcribed
        </p>
        <div class="toolbar">
          <button class="ghost-button" @click=${this.rename}>Rename</button>
          ${m.state === "recording"
            ? html`<button class="ghost-button" @click=${this.stopAndSummarize}>
                Stop & summarize
              </button>`
            : ""}
          <span class="grow"></span>
          <button class="danger-button" @click=${this.removeMeeting}>Delete</button>
        </div>
      </section>

      ${m.state === "failed"
        ? html`<div class="message-card message-card-error">
            <p>Failed: <span class="mono">${m.error ?? "unknown error"}</span></p>
          </div>`
        : ""}
      ${this.providerError
        ? html`<div class="message-card message-card-warn">
            <p>Provider: ${this.providerError}</p>
          </div>`
        : ""}

      ${m.state === "recording"
        ? html`<section>
            <h2>Recorder</h2>
            <mm-recorder .meetingId=${this.meetingId}></mm-recorder>
          </section>`
        : ""}
      ${m.state === "importing" && this.importProgress
        ? html`<section>
            <h2>Importing</h2>
            <div class="level-meter">
              <div
                class="level-meter-fill"
                style="width: ${Math.round(
                  (100 * this.importProgress.done) / this.importProgress.total,
                )}%"
              ></div>
            </div>
            <p class="muted" style="margin-top: 0.5rem">
              chunk ${this.importProgress.done} of ${this.importProgress.total}
            </p>
          </section>`
        : ""}

      <section>
        <h2>Transcript</h2>
        <mm-transcript
          .segments=${this.segments}
          .markers=${this.timelineEvents}
          .highlightMs=${this.highlightMs}
        ></mm-transcript>
      </section>

      <section>
        <h2>Summary</h2>
        <mm-summary .meetingId=${this.meetingId} .summary=${this.summary}></mm-summary>
      </section>
    `;
  }
}

customElements.define("mm-meeting-detail", MmMeetingDetail);
